import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { 
  Layout, Card, Button, Checkbox, Select, Tabs, Input, Space, 
  Divider, Typography, Row, Col, Menu, message, Spin, Badge
} from 'antd';
import { 
  DeleteOutlined, ShoppingCartOutlined, 
  UserOutlined, DollarOutlined, BankOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import withRoleAccess from '../../hoc/withRoleAccess';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import CurrentInventory from '../Inventory/CurrentInventory';
import InventoryPage from '../Inventory/InventoryPage';
import InventoryReport from '../Inventory/InventoryReport';
import './Sales.css';

const { Option } = Select;
const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Sider, Content } = Layout;

const SalesReport = lazy(() => import('./SalesReport'));

const ModernPOS = () => {
  const [activeTab, setActiveTab] = useState('pos');
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [items, setItems] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [vatChecked, setVatChecked] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  
  // Bank selection states
  const [bankDetails, setBankDetails] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [loadingBanks, setLoadingBanks] = useState(false);
  
  const [categories] = useState([
    'All', 'TEST', 'IELTS Book', 'TOEFL Book', 'GED Book', 
    'IGCSE Book', 'Other Book', 'Equipment', 'Others'
  ]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [loading, setLoading] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;

  const formatPrice = (price) => Number(price).toFixed(2);

  // API Calls
  const fetchStudents = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/students`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudents(data.students || []);
    } catch (error) {
      message.error('Failed to fetch students');
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/inventory`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(data.filter(item => item.status === 'active'));
    } catch (error) {
      message.error('Failed to fetch items');
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  const fetchBankDetails = useCallback(async () => {
    try {
      setLoadingBanks(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/bank-details`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setBankDetails(response.data);
    } catch (error) {
      console.error('[fetchBankDetails] Error fetching bank details:', error);
      message.error('Failed to fetch bank details');
    } finally {
      setLoadingBanks(false);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchStudents();
    fetchItems();
    fetchBankDetails(); // Fetch bank details on component mount
  }, [fetchStudents, fetchItems, fetchBankDetails]);

  // Handle payment method change
  const handlePaymentMethodChange = (value) => {
    setPaymentMethod(value);
    // Reset bank selection if not bank transfer
    if (value !== 'bank_transfer') {
      setSelectedBank(null);
    }
  };

  // Cart Management
  const addToCart = (item) => {
    const existingItem = cartItems.find(i => i.inventory_id === item.inventory_id);
    if (existingItem) {
      setCartItems(cartItems.map(i => 
        i.inventory_id === item.inventory_id 
          ? { ...i, quantity: i.quantity + 1 }
          : i
      ));
    } else {
      setCartItems([...cartItems, { ...item, quantity: 1 }]);
    }
    message.success(`${item.item_name} added to cart`);
  };

  const removeFromCart = (id) => {
    setCartItems(cartItems.filter(item => item.inventory_id !== id));
  };

  const updateQuantity = (id, quantity) => {
    setCartItems(cartItems.map(item => 
      item.inventory_id === id ? { ...item, quantity } : item
    ));
  };

  // Calculations
  const subtotal = cartItems.reduce((sum, item) => 
    sum + (item.price_per_unit * item.quantity), 0);
  const vat = vatChecked ? (subtotal - discount) * 0.07 : 0;
  const total = subtotal - discount + vat;

  // Purchase Handler
  const handlePurchase = async () => {
    if (!selectedStudent) return message.warning('Please select a student');
    if (!cartItems.length) return message.warning('Cart is empty');
    if (!paymentMethod) return message.warning('Please select payment method');
    
    // Check if bank transfer is selected but no bank is chosen
    if (paymentMethod === 'bank_transfer' && !selectedBank) {
      return message.warning('Please select a bank for bank transfer payment');
    }

    const saleData = {
      student_id: selectedStudent,
      items: cartItems,
      discount,
      vat,
      payment_method: paymentMethod,
      bank_detail_id: paymentMethod === 'bank_transfer' ? selectedBank : null,
    };

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/sales/log-sale`, saleData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      confirmAlert({
        title: 'Success',
        message: 'Purchase completed successfully!',
        buttons: [{ label: 'OK', onClick: () => resetCart() }]
      });
    } catch (error) {
      message.error('Purchase failed');
    } finally {
      setLoading(false);
    }
  };

  const resetCart = () => {
    setSelectedStudent(null);
    setCartItems([]);
    setDiscount(0);
    setVatChecked(false);
    setPaymentMethod(null);
    setSelectedBank(null);
  };

  // Check if purchase should be disabled
  const isPurchaseDisabled = () => {
    if (!cartItems.length || !selectedStudent || !paymentMethod) return true;
    if (paymentMethod === 'bank_transfer' && !selectedBank) return true;
    return false;
  };

  // Render Components
  const POSLayout = () => (
    <Layout className="pos-container">
      <Sider width={200} className="category-sider">
        <Menu
          mode="inline"
          selectedKeys={[selectedCategory]}
          onClick={({ key }) => setSelectedCategory(key)}
          items={categories.map(cat => ({
            key: cat,
            label: cat,
          }))}
        />
      </Sider>
      
      <Content className="items-content" style={{ padding: '16px' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center',
          minHeight: '100%'
        }}>
          <div style={{ 
            width: '100%', 
            maxWidth: '1200px'
          }}>
            <Row gutter={[16, 16]} justify="start">
              {items.filter(item => selectedCategory === 'All' || item.category === selectedCategory)
                .map(item => (
              <Col xs={24} sm={12} md={8} lg={6} key={item.inventory_id}>
                  <Card
                    hoverable
                    className="item-card"
                    onClick={() => addToCart(item)}
                    style={{
                      height: '280px',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    cover={
                      <div style={{
                        height: '120px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#fafafa',
                        borderBottom: '1px solid #f0f0f0'
                      }}>
                        <ShoppingCartOutlined style={{ fontSize: 32, color: '#bfbfbf' }} />
                      </div>
                    }
                  >
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      height: '100%',
                      textAlign: 'center'
                    }}>
                      <div style={{ marginBottom: '8px' }}>
                        <Text strong style={{ 
                          fontSize: '14px',
                          display: 'block',
                          marginBottom: '4px',
                          lineHeight: '1.4'
                        }}>
                          {item.item_name}
                        </Text>
                        <Text type="secondary" style={{ 
                          fontSize: '12px',
                          display: 'block',
                          lineHeight: '1.3'
                        }}>
                          {item.item_description}
                        </Text>
                      </div>
                      
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: 'auto'
                      }}>
                        <Text type="success" strong style={{ fontSize: '16px' }}>
                          {formatPrice(item.price_per_unit)} THB
                        </Text>
                        <Badge
                          count={item.category}
                          style={{
                            backgroundColor: '#29166f',
                            color: '#fff',
                            fontSize: '10px',
                            lineHeight: '16px',
                            height: '20px',
                            minWidth: '20px',
                            borderRadius: '10px'
                          }}
                        />
                      </div>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </div>
      </Content>

      <Sider width={350} className="cart-sider">
        <Card className="cart-card">
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Select
              showSearch
              placeholder="Select Customer"
              value={selectedStudent}
              onChange={setSelectedStudent}
              suffixIcon={<UserOutlined />}
              style={{ width: '100%' }}
            >
              {students.map(student => (
                <Option key={student.userid} value={student.userid}>
                  {student.nickname} {student.firstname} {student.lastname}
                </Option>
              ))}
            </Select>

            <div className="cart-items">
              {cartItems.map(item => (
                <div key={item.inventory_id} className="cart-item">
                  <Space>
                    <Text>{item.item_name}</Text>
                    <Input
                      type="number"
                      min={1}
                      value={item.quantity}
                      onChange={e => updateQuantity(item.inventory_id, parseInt(e.target.value))}
                      style={{ width: 60 }}
                    />
                    <Text>{formatPrice(item.price_per_unit * item.quantity)} THB</Text>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeFromCart(item.inventory_id)}
                    />
                  </Space>
                </div>
              ))}
            </div>

            <Divider />

            <Space direction="vertical" style={{ width: '100%' }}>
              <div className="summary-line">
                <Text>Subtotal</Text>
                <Text>{formatPrice(subtotal)} THB</Text>
              </div>
              <div className="summary-line">
                <Text>Discount</Text>
                <Input
                  value={discount}
                  onChange={e => setDiscount(parseFloat(e.target.value) || 0)}
                  style={{ width: 100 }}
                  suffix="THB"
                />
              </div>
              <div className="summary-line">
                <Checkbox checked={vatChecked} onChange={e => setVatChecked(e.target.checked)}>
                  VAT (7%)
                </Checkbox>
                <Text>{formatPrice(vat)} THB</Text>
              </div>
              <div className="summary-line total">
                <Title level={4}>Total</Title>
                <Title level={4}>{formatPrice(total)} THB</Title>
              </div>

              <Select
                placeholder="Payment Method"
                value={paymentMethod}
                onChange={handlePaymentMethodChange}
                style={{ width: '100%' }}
              >
                <Option value="cash">Cash</Option>
                <Option value="bank_transfer">Bank Transfer</Option>
                <Option value="credit_card">Credit Card</Option>
                <Option value="online_payment">Online Payment</Option>
              </Select>

              {/* Bank Selection - Only show when Bank Transfer is selected */}
              {paymentMethod === 'bank_transfer' && (
                <div style={{ marginBottom: '12px' }}>
                  <Text strong style={{ display: 'block', marginBottom: '8px', color: '#29166f' }}>
                    Select Bank Account:
                  </Text>
                  <Select
                    showSearch
                    placeholder="Choose bank account..."
                    value={selectedBank}
                    onChange={setSelectedBank}
                    loading={loadingBanks}
                    style={{ width: '100%' }}
                    suffixIcon={<BankOutlined />}
                    filterOption={(input, option) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {bankDetails.map(bank => (
                      <Option key={bank.id} value={bank.id}>
                        <div>
                          <div style={{ fontWeight: 'bold' }}>
                            {bank.bank_name} ({bank.account_number})
                          </div>
                        </div>
                      </Option>
                    ))}
                  </Select>
                  
                  {/* Selected Bank Info - Compact Display */}
                  {selectedBank && (
                    <div style={{
                      marginTop: '8px',
                      padding: '8px 12px',
                      backgroundColor: '#f6ffed',
                      border: '1px solid #b7eb8f',
                      borderRadius: '6px'
                    }}>
                      {(() => {
                        const bank = bankDetails.find(b => b.id === selectedBank);
                        return bank ? (
                          <div>
                            <Text strong style={{ color: '#52c41a', fontSize: '12px' }}>
                              Selected Bank:
                            </Text>
                            <div style={{ fontSize: '11px', marginTop: '2px' }}>
                              <div><Text strong>{bank.bank_name}</Text></div>
                              <div><Text>{bank.account_name}</Text></div>
                              <div><Text type="secondary">{bank.account_number}</Text></div>
                            </div>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  )}
                </div>
              )}

              <Button
                type="primary"
                size="large"
                block
                icon={<DollarOutlined />}
                onClick={handlePurchase}
                disabled={isPurchaseDisabled()}
                loading={loading}
              >
                Process Payment
              </Button>
            </Space>
          </Space>
        </Card>
      </Sider>
    </Layout>
  );

  return (
    <Layout className="pos-system">
      <Sidebar />
      <Layout>
        <Header />
        <Content>
          <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={[
                {
                  key: 'pos',
                  label: (
                    <>
                      <ShoppingCartOutlined /> POS
                    </>
                  ),
                  children: loading ? <Spin size="large" /> : <POSLayout />,
                },
                {
                  key: 'salesReport',
                  label: 'Sales Report',
                  children: (
                    <Suspense fallback={<Spin size="large" />}>
                      <SalesReport />
                    </Suspense>
                  ),
                },
                {
                  key: 'currentInventory',
                  label: 'Current Inventory',
                  children: activeTab === 'currentInventory' && <CurrentInventory />,
                },
                {
                  key: 'report',
                  label: 'Inventory Report',
                  children: activeTab === 'report' && <InventoryReport />,
                },
                {
                  key: 'add',
                  label: 'Add Inventory',
                  children: activeTab === 'add' && <InventoryPage />,
                },
              ]}
            />
        </Content>
      </Layout>
    </Layout>
  );
};

export default withRoleAccess(ModernPOS, ['superadmin', 'admin']);